import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { deleteUser, deleteUserFromCompany, getCompanyUser, updateUser } from '@/api/auth';

import { CompanyUser, InviteUserForm } from '@/interface';

interface MemberErrorPayload {
  message: string;
  [key: string]: unknown;
}

const toMemberErrorPayload = (error: unknown, fallbackMessage: string): MemberErrorPayload => {
  const data = axios.isAxiosError(error) ? (error.response?.data as Partial<MemberErrorPayload> | undefined) : undefined;
  return { ...data, message: data?.message || fallbackMessage };
};

interface MemberState {
  loading: boolean;
  error: MemberErrorPayload | null;
  members: CompanyUser[];
  success?: boolean;
}

const initialState: MemberState = {
  loading: false,
  error: null,
  members: [],
  success: false,
};

export const getMembersRequest = createAsyncThunk<CompanyUser[], string, { rejectValue: MemberErrorPayload }>(
  'getMembers',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getCompanyUser(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toMemberErrorPayload(error, 'Failed to fetch project details'));
    }
  },
);
export const updateUserRequest = createAsyncThunk<
  CompanyUser,
  { id: string; data: InviteUserForm },
  { rejectValue: MemberErrorPayload }
>('updateUserRequest', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateUser(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(toMemberErrorPayload(error, 'Failed to create project'));
  }
});

export const deleteUserRequest = createAsyncThunk<unknown, string, { rejectValue: MemberErrorPayload }>(
  'user/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteUser(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toMemberErrorPayload(error, 'Failed to delete expert'));
    }
  },
);

export const deleteUserFromCompanyRequest = createAsyncThunk<{ message?: string }, string, { rejectValue: MemberErrorPayload }>(
  'user/deleteFromCompany',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteUserFromCompany(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(toMemberErrorPayload(error, 'Failed to delete user from company'));
    }
  },
);
export const memberSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    resetUpdateSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getMembersRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMembersRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload;
      })
      .addCase(getMembersRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateUserRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUserRequest.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateUserRequest.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteUserFromCompanyRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserFromCompanyRequest.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteUserFromCompanyRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});

export const { resetUpdateSuccess } = memberSlice.actions;
